import React, { useState } from "react";
import { Panel } from "@/components/common/Panel";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PlatformControls } from "@/components/common/PlatformControls";
import { KeyRound, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
    const { user } = useAuth();
    const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const changePassword = async (e) => {
        e.preventDefault();
        setError("");
        if (pw.next !== pw.confirm) return setError("Passwords do not match");
        if (pw.next.length < 8) return setError("New password must be at least 8 characters");
        setLoading(true);
        try {
            await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
            toast.success("Password updated");
            setPw({ current: "", next: "", confirm: "" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Update failed";
            setError(typeof msg === "string" ? msg : "Update failed");
            toast.error(msg);
        } finally { setLoading(false); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel eyebrow="Identity" title="Account">
                <div className="space-y-3">
                    <Row label="Name" value={user?.name} />
                    <Row label="Email" value={user?.email} />
                    <Row label="Mobile" value={user?.mobile} />
                    <Row label="Roles" value={(user?.roles || []).join(" · ")} />
                    {user?.is_super_admin && <Row label="Tier" value="PLATFORM OWNER" />}
                </div>
            </Panel>

            <Panel eyebrow="Security" title="Change Password" testId="change-password-panel">
                <form onSubmit={changePassword} className="space-y-3">
                    <PwField label="Current Password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} testid="change-pw-current" />
                    <PwField label="New Password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} testid="change-pw-new" />
                    <PwField label="Confirm New Password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} testid="change-pw-confirm" />
                    {error && (
                        <div className="flex items-start gap-2 p-3 border border-rose-500/30 bg-rose-500/5 text-rose-300 text-xs rounded-sm">
                            <AlertCircle size={14} className="mt-0.5" /><span>{error}</span>
                        </div>
                    )}
                    <button data-testid="change-pw-submit" type="submit" disabled={loading} className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
                        <Save size={12} /> {loading ? "Updating…" : "Update Password"}
                    </button>
                </form>
            </Panel>

            <PlatformControls />

        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between panel px-4 py-2.5">
            <span className="label-eyebrow">{label}</span>
            <span className="text-sm text-white font-mono">{value || "—"}</span>
        </div>
    );
}

function PwField({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5 flex items-center gap-1.5"><KeyRound size={10} /> {label}</label>
            <input
                data-testid={testid}
                type="password"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="field"
                required
            />
        </div>
    );
}
